import type { Workspace } from './workspace';
import { t } from '@lumen/platform';

/** Page access levels, weakest first (P-481). `edit_content` only differs from `edit` on databases. */
export const ACCESS_LEVELS = ['none', 'view', 'comment', 'edit_content', 'edit', 'full'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export function atLeast(level: AccessLevel, min: AccessLevel): boolean {
  return ACCESS_LEVELS.indexOf(level) >= ACCESS_LEVELS.indexOf(min);
}

export function maxLevel(a: AccessLevel, b: AccessLevel): AccessLevel {
  return atLeast(a, b) ? a : b;
}

export const ACCESS_LABELS: Record<AccessLevel, string> = {
  full: t('Full access'),
  edit: t('Can edit'),
  edit_content: t('Can edit content'),
  comment: t('Can comment'),
  view: t('Can view'),
  none: t('No access'),
};

/** A sharing grant on a page. `principal` is `user:<id>` or `workspace:<id>` ("Anyone in workspace"). */
export interface Grant {
  pageId: string;
  principal: string;
  level: AccessLevel;
}

export interface PublishOptions {
  allowEditing?: boolean;
  allowComments?: boolean;
  allowDuplicate?: boolean;
  /** Search engine indexing; when off the published page carries `noindex`. */
  indexing?: boolean;
  /** Epoch ms after which the public link stops working, or null. */
  expiresAt?: number | null;
  /** Search engines and link previews (P-972); empty fields take the page's title, text and cover. */
  seo?: { title?: string; description?: string; image?: string };
}

export interface Published {
  pageId: string;
  options: PublishOptions;
}

export type WorkspaceRole = 'owner' | 'member' | 'guest';

export interface AccessData {
  wsId: string;
  grants: Grant[];
  published: Published[];
  /** Workspace role per user id. */
  roles: Map<string, WorkspaceRole>;
}

/** The page and its ancestors, nearest first. */
export function chainOf(ws: Workspace, pageId: string): string[] {
  return ws.getPage(pageId) ? [pageId, ...ws.ancestors(pageId).map((p) => p.id).reverse()] : [pageId];
}

/**
 * The page whose grants apply to `pageId`: the nearest page in its chain that has grants of its own.
 * Subpages inherit their parent's permissions until they get grants themselves (P-484).
 */
export function permissionRoot(ws: Workspace, grants: Grant[], pageId: string): string | null {
  const withGrants = new Set(grants.map((g) => g.pageId));
  return chainOf(ws, pageId).find((id) => withGrants.has(id)) ?? null;
}

/** The grants in effect on a page (its own, or inherited from its permission root). */
export function effectiveGrants(ws: Workspace, grants: Grant[], pageId: string): { grants: Grant[]; inheritedFrom: string | null } {
  const root = permissionRoot(ws, grants, pageId);
  if (!root) return { grants: [], inheritedFrom: null };
  return { grants: grants.filter((g) => g.pageId === root), inheritedFrom: root === pageId ? null : root };
}

/** The nearest published page in the chain whose link has not expired. */
export function publishedRoot(ws: Workspace, published: Published[], pageId: string, now = Date.now()): Published | null {
  for (const id of chainOf(ws, pageId)) {
    const p = published.find((x) => x.pageId === id);
    if (p) return !p.options.expiresAt || p.options.expiresAt > now ? p : null;
  }
  return null;
}

export function publishedLevel(p: Published | null): AccessLevel {
  if (!p) return 'none';
  return p.options.allowEditing ? 'edit' : p.options.allowComments ? 'comment' : 'view';
}

/**
 * A user's access to a page (null user = anonymous web visitor):
 * - published pages (and their subpages) give everyone the published level;
 * - the owner of a private page always has full access;
 * - an explicit grant for the user on the permission root sets their level (even "No access");
 * - otherwise members get full access to pages in teamspaces they can see, plus "Anyone in workspace";
 * - guests only get what is shared with them.
 */
export function resolvePageAccess(ws: Workspace, data: AccessData, userId: string | null, pageId: string, now = Date.now()): AccessLevel {
  const pub = publishedLevel(publishedRoot(ws, data.published, pageId, now));
  const role = userId ? data.roles.get(userId) : undefined;
  if (!userId || !role) return pub;
  const page = ws.getPage(pageId);
  if (!page) return role === 'guest' ? pub : 'full';

  const rootKey = ws.rootKeyOf(pageId);
  if (rootKey === `private:${userId}`) return 'full';

  let base: AccessLevel = 'none';
  if (role !== 'guest') {
    if (rootKey?.startsWith('team:')) {
      const t = ws.getTeamspace(rootKey.slice(5));
      base = !t || t.access === 'open' || t.members[userId] ? 'full' : 'none';
    } else if (!rootKey) base = 'full';
  }

  const { grants } = effectiveGrants(ws, data.grants, pageId);
  const forUser = grants.find((g) => g.principal === `user:${userId}`);
  const forWs = role !== 'guest' ? grants.find((g) => g.principal === `workspace:${data.wsId}`) : undefined;
  let level = forUser ? forUser.level : forWs ? maxLevel(base, forWs.level) : base;
  if (forUser && forWs && forUser.level !== 'none') level = maxLevel(forUser.level, forWs.level);
  return maxLevel(level, pub);
}

/**
 * Page ids shown under "Shared" (P-043): pages shared with the user explicitly that they would not
 * otherwise see in Private or their teamspaces, topmost only.
 */
export function sharedRoots(ws: Workspace, data: AccessData, userId: string): string[] {
  const role = data.roles.get(userId);
  if (!role) return [];
  const explicit = new Set(data.grants.filter((g) => g.principal === `user:${userId}` && g.level !== 'none').map((g) => g.pageId));
  const out: string[] = [];
  for (const id of explicit) {
    const page = ws.getPage(id);
    if (!page || page.trashed) continue;
    const key = ws.rootKeyOf(id);
    if (key === `private:${userId}`) continue;
    if (role !== 'guest' && key?.startsWith('team:')) {
      const t = ws.getTeamspace(key.slice(5));
      if (!t || t.access === 'open' || t.members[userId]) continue;
    }
    // Only the topmost shared page of a shared subtree.
    if (ws.ancestors(id).some((a) => explicit.has(a.id))) continue;
    out.push(id);
  }
  return out;
}

export type ShareChange = { kind: 'set'; principal: string; level: AccessLevel } | { kind: 'remove'; principal: string } | { kind: 'restore' };

/**
 * Applies a change to a page's sharing and returns the page's own grants afterwards. Changing a
 * subpage that inherits its permissions first copies the inherited grants onto it, so it stops
 * inheriting ("overridden"); "restore" drops its own grants so it inherits again (P-484).
 */
export function applyShareChange(ws: Workspace, grants: Grant[], pageId: string, change: ShareChange): Grant[] {
  if (change.kind === 'restore') return [];
  const { grants: current } = effectiveGrants(ws, grants, pageId);
  let own = current.map((g) => ({ ...g, pageId }));
  own = own.filter((g) => g.principal !== change.principal);
  if (change.kind === 'set') own.push({ pageId, principal: change.principal, level: change.level });
  return own;
}

/** True when a subpage has its own permissions instead of inheriting its parent's. */
export function isOverridden(ws: Workspace, grants: Grant[], pageId: string): boolean {
  return grants.some((g) => g.pageId === pageId) && ws.ancestors(pageId).length > 0;
}
